import {
    createShelfLocation,
    deleteShelfLocation,
    listShelfLocations,
    updateShelfLocation,
    type ShelfLocation,
} from '@api/warehouse-ops';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import {
    ActionIcon,
    Badge,
    Button,
    Group,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FC } from 'react';

type Props = { warehouseId: string; warehouseName: string };

type ShelfRow = ShelfLocation & { _editing?: boolean };

const ShelfLocationManager: FC<Props> = ({ warehouseId, warehouseName }) => {
    const queryClient = useQueryClient();
    const [newCode, setNewCode] = useState('');
    const [newName, setNewName] = useState('');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState('');

    const qk = ['shelf-locations', warehouseId];

    const { data: locations = [], isLoading } = useQuery<ShelfLocation[]>({
        queryKey: qk,
        queryFn: () => listShelfLocations(warehouseId),
    });

    const createMutation = useMutation({
        mutationFn: () => createShelfLocation({ warehouseId, code: newCode.trim(), name: newName.trim() || undefined }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qk });
            setNewCode('');
            setNewName('');
        },
    });

    const updateMutation = useMutation({
        mutationFn: (id: string) => updateShelfLocation(id, { name: editName.trim() || undefined }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: qk });
            setEditingId(null);
        },
    });

    const deactivateMutation = useMutation({
        mutationFn: (id: string) => deleteShelfLocation(id),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: qk }),
    });

    const columns: DataSheetColumn<ShelfRow>[] = [
        {
            key: 'code',
            header: 'Code',
            width: 120,
            sticky: true,
            render: (_, row) => <Text fw={500} size="sm">{row.code}</Text>,
        },
        {
            key: 'name',
            header: 'Name',
            width: 200,
            render: (_, row) => {
                if (editingId === row.id) {
                    return (
                        <Group gap="xs">
                            <TextInput
                                size="xs"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                            />
                            <Button size="xs" onClick={() => updateMutation.mutate(row.id)}>Save</Button>
                            <Button size="xs" variant="subtle" onClick={() => setEditingId(null)}>Cancel</Button>
                        </Group>
                    );
                }
                return <Text size="sm">{row.name ?? '—'}</Text>;
            },
        },
        {
            key: 'isActive',
            header: 'Status',
            width: 90,
            render: (_, row) => (
                <Badge color={row.isActive ? 'green' : 'gray'} size="sm">
                    {row.isActive ? 'Active' : 'Inactive'}
                </Badge>
            ),
        },
        {
            key: '_actions',
            header: '',
            width: 80,
            align: 'right',
            render: (_, row) => (
                <Group gap="xs" justify="flex-end">
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        onClick={() => { setEditingId(row.id); setEditName(row.name ?? ''); }}
                    >
                        <PencilSimple size={14} />
                    </ActionIcon>
                    <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => deactivateMutation.mutate(row.id)}
                    >
                        <Trash size={14} />
                    </ActionIcon>
                </Group>
            ),
        },
    ];

    return (
        <Stack gap="sm">
            <Title order={5}>{warehouseName} — Shelf Locations</Title>
            <DataSheet<ShelfRow>
                data={locations}
                columns={columns}
                isLoading={isLoading}
                hideHeaderBar
                maxHeight="auto"
                getRowKey={(row) => row.id}
                emptyState={<Text size="sm" c="dimmed" ta="center" py="lg">No shelf locations yet. Add one below.</Text>}
            />
            <Group gap="sm" align="flex-end">
                <TextInput
                    label="Code"
                    placeholder="A-01-01"
                    size="xs"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value)}
                />
                <TextInput
                    label="Name (optional)"
                    placeholder="Aisle 1, Row 1"
                    size="xs"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                />
                <Button
                    size="xs"
                    leftSection={<Plus size={12} />}
                    disabled={!newCode.trim()}
                    loading={createMutation.isPending}
                    onClick={() => createMutation.mutate()}
                >
                    Add Shelf
                </Button>
            </Group>
        </Stack>
    );
};

export default ShelfLocationManager;
